import { getActiveCrawls, getCrawlStatus } from './api';

export interface CrawlStatus {
  id: string;
  status: 'initializing' | 'in_progress' | 'crawling' | 'scraping' | 'processing' | 'embedding' | 'completed' | 'failed' | 'no_crawl' | 'stopped' | 'aborted';
  url: string;
  max_pages: number;
  pages_crawled: number;
  pages_in_queue: number;
  pages_remaining?: number;
  pages_failed?: number;
  pages_scraped?: number;
  total_pages?: number;
  estimated_total_pages?: number;
  current_url?: string;
  visited_urls?: string[];
  newly_visited_urls?: string[];
  latest_url?: string;
  visited_urls_total?: number;
  visited_urls_trimmed?: boolean;
  pending_urls?: string[];
  errors?: string[];
  progress_percentage?: number;
  embedding_status?: 'pending' | 'queued' | 'in_progress' | 'completed' | 'failed' | 'skipped';
  embedding_progress?: number;
  embedding_current?: number;
  embedding_total?: number;
  chunk_index?: number | null;
  chunk_urls_planned?: number;
  chunks_total?: number;
  chunks_completed?: number;
  chunk_label?: string | null;
  last_completed_chunk?: string | null;
  chunk_concurrency?: number;
  start_time?: string;
  end_time?: string;
  last_updated?: string;
  message?: string;
  clientId: string;
  hidden?: boolean;
}

const LOCAL_STORAGE_KEY = 'activeCrawlJobs';
const HISTORY_STORAGE_KEY = 'crawlJobHistory';
const NO_CRAWL_CACHE_KEY = 'noCrawlCache';

// Maximum number of historical jobs to keep per client
const MAX_HISTORY_PER_CLIENT = 5;

// Cache expiration time for 'no_crawl' status (30 minutes in milliseconds)
const NO_CRAWL_CACHE_TTL = 30 * 60 * 1000;

// Observer pattern for real-time updates
const observers: ((status: CrawlStatus[]) => void)[] = [];

// Get the 'no_crawl' cache from localStorage
export const getNoCrawlCache = (): Record<string, number> => {
  const cacheData = localStorage.getItem(NO_CRAWL_CACHE_KEY);
  return cacheData ? JSON.parse(cacheData) : {};
};

// Check if a client ID is in the 'no_crawl' cache and not expired
export const isInNoCrawlCache = (clientId: string): boolean => {
  const cache = getNoCrawlCache();
  const timestamp = cache[clientId];
  
  if (!timestamp) return false;
  
  // Check if the cache entry is still valid (not expired)
  return Date.now() - timestamp < NO_CRAWL_CACHE_TTL;
};

// Add a client ID to the 'no_crawl' cache
export const addToNoCrawlCache = (clientId: string): void => {
  const cache = getNoCrawlCache();
  cache[clientId] = Date.now();
  localStorage.setItem(NO_CRAWL_CACHE_KEY, JSON.stringify(cache));
};

// Clean up expired entries from the 'no_crawl' cache
export const cleanupNoCrawlCache = (): void => {
  const cache = getNoCrawlCache();
  const now = Date.now();
  let hasChanges = false;
  
  Object.keys(cache).forEach(clientId => {
    if (now - cache[clientId] > NO_CRAWL_CACHE_TTL) {
      delete cache[clientId];
      hasChanges = true;
    }
  });
  
  if (hasChanges) {
    localStorage.setItem(NO_CRAWL_CACHE_KEY, JSON.stringify(cache));
  }
};

// Get current active crawl jobs from localStorage
export const getActiveCrawlJobs = (): CrawlStatus[] => {
  const storedData = localStorage.getItem(LOCAL_STORAGE_KEY);
  return storedData ? JSON.parse(storedData) : [];
};

// Get crawl job history from localStorage
export const getCrawlJobHistory = (clientId?: string): CrawlStatus[] => {
  const storedData = localStorage.getItem(HISTORY_STORAGE_KEY);
  const history = storedData ? JSON.parse(storedData) : [];
  
  // Return all history or filter by client ID
  return clientId ? history.filter((job: CrawlStatus) => job.clientId === clientId) : history;
};

// Store a completed job in history
export const storeCrawlJobHistory = (job: CrawlStatus) => {
  // Only store completed or failed jobs
  if (job.status !== 'completed' && job.status !== 'failed') {
    return;
  }
  
  const history = getCrawlJobHistory();
  
  // Check if this job is already in history
  const existingIndex = history.findIndex(h => h.id === job.id);
  if (existingIndex >= 0) {
    // Update existing record
    history[existingIndex] = job;
  } else {
    // Add to history
    history.push(job);
  }
  
  // Keep only the most recent MAX_HISTORY_PER_CLIENT jobs per client
  const clientJobs = history.filter(h => h.clientId === job.clientId);
  if (clientJobs.length > MAX_HISTORY_PER_CLIENT) {
    // Sort by end_time descending
    clientJobs.sort((a, b) => {
      const aTime = a.end_time ? new Date(a.end_time).getTime() : 0;
      const bTime = b.end_time ? new Date(b.end_time).getTime() : 0;
      return bTime - aTime;
    });
    
    // Keep only the most recent jobs
    const jobsToKeep = clientJobs.slice(0, MAX_HISTORY_PER_CLIENT).map(j => j.id);
    
    // Filter history to remove old jobs for this client
    const updatedHistory = history.filter(h => 
      h.clientId !== job.clientId || jobsToKeep.includes(h.id)
    );
    
    // Store updated history
    localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(updatedHistory));
  } else {
    // Store history
    localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(history));
  }
};

// Clear crawl job history for a client
export const clearCrawlJobHistory = (clientId: string) => {
  const history = getCrawlJobHistory();
  const updatedHistory = history.filter(job => job.clientId !== clientId);
  localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(updatedHistory));
};

// Clear all crawl jobs (useful for debugging)
export const clearAllCrawlJobs = () => {
  localStorage.removeItem(LOCAL_STORAGE_KEY);
  notifyObservers();
};

// Add a new crawl job
export const addCrawlJob = (jobData: Partial<CrawlStatus> & { clientId: string; id: string }) => {
  const activeCrawls = getActiveCrawlJobs();
  
  // Check if job already exists, update it if it does
  const existingIndex = activeCrawls.findIndex(job => job.id === jobData.id);
  if (existingIndex >= 0) {
    activeCrawls[existingIndex] = { ...activeCrawls[existingIndex], ...jobData };
  } else {
    // First, clear any existing jobs for this client
    // This ensures we don't have multiple active jobs for the same client
    const clientJobs = activeCrawls.filter(job => job.clientId === jobData.clientId);
    if (clientJobs.length > 0) {
      // Move existing jobs to history if they're complete or failed
      clientJobs.forEach(job => {
        if (job.status === 'completed' || job.status === 'failed') {
          storeCrawlJobHistory(job);
        }
      });
      
      // Remove existing jobs for this client
      const updatedCrawls = activeCrawls.filter(job => job.clientId !== jobData.clientId);
      activeCrawls.length = 0;
      activeCrawls.push(...updatedCrawls);
    }
    
    // Initialize new job with basic status
    const newJob: CrawlStatus = {
      status: 'initializing',
      url: jobData.url || '',
      max_pages: jobData.max_pages || 20,
      pages_crawled: 0,
      pages_in_queue: 1,
      progress_percentage: 0,
      visited_urls: [],
      pending_urls: [],
      errors: [],
      start_time: new Date().toISOString(),
      ...jobData
    };
    activeCrawls.push(newJob);
    
    // Remove this client ID from the no_crawl cache if present
    const noCrawlCache = getNoCrawlCache();
    if (noCrawlCache[jobData.clientId]) {
      delete noCrawlCache[jobData.clientId];
      localStorage.setItem(NO_CRAWL_CACHE_KEY, JSON.stringify(noCrawlCache));
    }
  }
  
  localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(activeCrawls));
  notifyObservers();
  
  return jobData.id;
};

// Update an existing crawl job
export const updateCrawlJob = (id: string, updatedData: Partial<CrawlStatus>) => {
  const activeCrawls = getActiveCrawlJobs();
  const jobIndex = activeCrawls.findIndex(job => job.id === id);
  
  if (jobIndex >= 0) {
    // Apply updates
    const updatedJob = { ...activeCrawls[jobIndex], ...updatedData };
    activeCrawls[jobIndex] = updatedJob;
    
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(activeCrawls));
    notifyObservers();
    
    // If the job is now completed, failed, stopped, or aborted, add to history and remove from active crawls
    if (updatedData.status === 'completed' || updatedData.status === 'failed' || updatedData.status === 'stopped' || updatedData.status === 'aborted') {
      storeCrawlJobHistory(updatedJob);

      // After adding to history, remove from active crawls IMMEDIATELY instead of just marking as hidden
      setTimeout(() => {
        const currentCrawls = getActiveCrawlJobs();
        const filteredCrawls = currentCrawls.filter(job => job.id !== id);
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(filteredCrawls));
        notifyObservers();
      }, 1000); // Very short delay to ensure UI has time to process the completion
    }
    
    return true;
  }
  
  return false;
};

// Remove a crawl job
export const removeCrawlJob = (id: string) => {
  const activeCrawls = getActiveCrawlJobs();
  const job = activeCrawls.find(job => job.id === id);
  const updatedCrawls = activeCrawls.filter(job => job.id !== id);
  
  if (updatedCrawls.length !== activeCrawls.length) {
    // If removing a completed/failed/stopped/aborted job, store in history
    if (job && (job.status === 'completed' || job.status === 'failed' || job.status === 'stopped' || job.status === 'aborted')) {
      storeCrawlJobHistory(job);
    }
    
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(updatedCrawls));
    notifyObservers();
    return true;
  }
  return false;
};

// Mark a job as hidden (but keep the data for a short time)
export const hideCrawlJob = (id: string) => {
  const result = updateCrawlJob(id, { hidden: true });
  
  // Schedule actual removal after a delay
  if (result) {
    setTimeout(() => {
      removeCrawlJob(id);
    }, 10000); // Remove after 10 seconds
  }
  
  return result;
};

// Register an observer for status changes
export const subscribeToStatusUpdates = (callback: (status: CrawlStatus[]) => void) => {
  observers.push(callback);
  return () => {
    const index = observers.indexOf(callback);
    if (index > -1) {
      observers.splice(index, 1);
    }
  };
};

// Notify all observers of status changes
const notifyObservers = () => {
  const status = getActiveCrawlJobs();
  observers.forEach(callback => callback(status));
};

// Function to check server for active crawl jobs
export const checkServerForActiveCrawls = async () => {
  try {
    // Clean up expired entries from the no_crawl cache
    cleanupNoCrawlCache();
    
    // Get the latest crawl status from the server for each active job
    const result = await getActiveCrawls();
    if (result && result.active_crawls && Array.isArray(result.active_crawls)) {
      
      // For each active crawl reported by the server, check if we're tracking it
      const localCrawls = getActiveCrawlJobs();
      let hasChanges = false;
      
      for (const clientId of result.active_crawls) {
        // Skip clients that are in the no_crawl cache
        if (isInNoCrawlCache(clientId)) {
              continue;
        }
        
        // Check if we're already tracking this client
        const existingJob = localCrawls.find(job => job.clientId === clientId && !job.hidden);
        if (!existingJob) {
          try {
            // Fetch the status for this client
            const status = await getCrawlStatus(clientId);
            
            // If it's a valid crawl status, add it to our tracking
            if (status && status.status && status.status !== 'no_crawl') {
              
              // Add to active crawls
              addCrawlJob({
                id: status.id || `crawl-${clientId}`,
                clientId: clientId,
                status: status.status,
                url: status.url || '',
                max_pages: status.max_pages || 20,
                pages_crawled: status.pages_crawled || 0,
                progress_percentage: status.progress_percentage || 0
              });
              
              hasChanges = true;
            } else {
              // If we get a no_crawl response, add to cache
              addToNoCrawlCache(clientId);
            }
          } catch (error) {
            console.error(`[CRAWL-SERVICE] Error checking status for client ${clientId}:`, error);
          }
        }
      }
      
      return hasChanges;
    }
    
    return false;
    
  } catch (error) {
    console.error(`[CRAWL-SERVICE] Error checking server for active crawls:`, error);
    return false;
  }
};

// Check individual client crawl status with caching for 'no_crawl' responses
export const checkClientCrawlStatus = async (clientId: string): Promise<boolean> => {
  // Skip check if client is in 'no_crawl' cache
  if (isInNoCrawlCache(clientId)) {
    return false;
  }
  
  try {
    const status = await getCrawlStatus(clientId);
    
    // If we get 'no_crawl', add to cache
    if (status && status.status === 'no_crawl') {
      addToNoCrawlCache(clientId);
      return false;
    }
    
    return true;
  } catch (error) {
    console.error(`[CRAWL-SERVICE] Error checking status for client ${clientId}:`, error);
    return false;
  }
};

// Clean up stale crawl jobs
export const cleanupStaleCrawlJobs = () => {
  const activeCrawls = getActiveCrawlJobs();
  const now = Date.now();
  let hasChanges = false;
  
  const filteredCrawls = activeCrawls.filter(job => {
    // Remove completed/failed/stopped/aborted jobs
    if (['completed', 'failed', 'stopped', 'aborted'].includes(job.status)) {
      // Store in history before removing
      storeCrawlJobHistory(job);
      hasChanges = true;
      return false; // Remove from active
    }
    
    // Remove jobs older than 1 hour that are still initializing/in_progress
    if (job.start_time) {
      const jobAge = now - new Date(job.start_time).getTime();
      if (jobAge > 3600000) { // 1 hour
        hasChanges = true;
        return false; // Remove stale job
      }
    }
    
    return true; // Keep active jobs
  });
  
  if (hasChanges) {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(filteredCrawls));
    notifyObservers();
  }
};

// Initialize service on import
(function initialize() {
  // Clean up stale jobs immediately
  cleanupStaleCrawlJobs();
  
  // Check for active crawls immediately
  checkServerForActiveCrawls();
  
  // Clean up stale jobs every minute
  setInterval(cleanupStaleCrawlJobs, 60000);
  
  // Check for active crawls every 5 minutes instead of 2 minutes
  // This is enough since we're using EventSource for real-time updates
  setInterval(checkServerForActiveCrawls, 300000);
})(); 
